import React, { useEffect, useRef, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  Users,
  CalendarDays,
  FileText,
  Image,
  Newspaper,
  Megaphone,
  Sparkles,
  HardDrive,
  Heart,
  ClipboardCheck,
  MessagesSquare,
  BookUser,
  Trash2,
  MessageCircle,
  Compass,
} from "lucide-react";

const logo = "/assets/logo.png";
const userImage = "/assets/image_one.png";
const adminImage = "/assets/image_three.png";
const organizationImage = "/assets/image_two.png";
const mobileMockup = "/assets/mobile_mockup.png";
const whoForImage = "/assets/images/whos_for.png";
const footerLogo = "/assets/images/bharath_white_logo.png";

// Colors pulled from the Bharat Sangam mark, used for the hero orbit.
const ORBIT_COLORS = [
  "#59C6EF",
  "#7B9FE0",
  "#B39BD6",
  "#E8432E",
  "#DE3D95",
  "#F0954A",
  "#82BE66",
];

const contact = {
  company: "Politica Bharat Corporation LLP",
  email: "info@politicalp.com",
  phone: "+91 73490 32445",
  address:
    "Door No.2-159/3B, Sonal Complex Golikatte Road, Nandalike, Udupi, Karnataka - 576111",
  gstin: "29ABIFP8398K1Z5",
};


const features = [
  [
    Users,
    "Member Management",
    "Track members, roles and approvals in one directory instead of scattered spreadsheets.",
  ],
  [
    Newspaper,
    "News & Announcements",
    "Publish updates for members and followers so nobody misses what the club decided.",
  ],
  [
    Sparkles,
    "AI Poster Design",
    "Turn an announcement into a shareable poster in seconds, no designer needed.",
  ],
  [
    Image,
    "Media Uploads",
    "Keep photos and videos from every event organised in one shared album.",
  ],
  [
    MessageCircle,
    "WhatsApp & SMS",
    "Send meeting reminders and urgent notices straight to members' phones.",
  ],
  [
    CalendarDays,
    "Event Management",
    "Plan events, track RSVPs and manage schedules without a dozen phone calls.",
  ],
  [
    ClipboardCheck,
    "Meetings",
    "Set agendas, mark attendance and keep a record of every meeting.",
  ],
  [
    MessagesSquare,
    "Discussions",
    "Give members a place to raise ideas and settle decisions in the open.",
  ],
  [
    FileText,
    "Forms",
    "Collect registrations, feedback or applications with forms members fill in a minute.",
  ],
  [
    HardDrive,
    "Drive",
    "Store certificates, minutes and records where every office-bearer can find them.",
  ],
  [
    Heart,
    "Matrimony",
    "Help members' families connect within the community — the old custom, organised.",
  ],
  [
    Megaphone,
    "Advertisement",
    "Carry sponsor ads inside your news and posts, and earn from every campaign.",
  ],
  [
    BookUser,
    "Address Book",
    "Keep a living directory of every member's contact and address, always current.",
  ],
];

const clubTypes = [
  "Youth Clubs",
  "Regional & Caste Associations",
  "Charitable Trusts",
  "Cultural Sanghas",
  "National Federations",
  "International Chapters",
];

function supportMailto(subject = "Bharat Sangam Support Request") {
  const body = `Hello Bharat Sangam Team,

Club / Organization Name:
Username:
Registered Mobile Number:
Request Details:

Thank you.`;

  return `mailto:${contact.email}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

function goTo(path) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("popstate"));

  setTimeout(() => {
    if (window.location.hash) {
      document.querySelector(window.location.hash)?.scrollIntoView({
        behavior: "smooth",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, 20);
}

function useReveal() {
  useEffect(() => {
    const items = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("show");
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);
}

function Header() {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/95 backdrop-blur">
      <div className="mx-auto flex min-h-[72px] max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <button
          onClick={() => goTo("/")}
          className="flex min-w-0 items-center"
          aria-label="Go to Bharat Sangam home"
        >
          <img
            src={logo}
            alt="Bharat Sangam"
            className="h-11 w-auto max-w-[145px] object-contain sm:h-14 sm:max-w-none md:h-16"
          />
        </button>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <a
            href="https://www.bsangam.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-blue-900 px-3 py-2 text-xs font-semibold text-blue-900 transition hover:bg-blue-900 hover:text-white sm:px-5 sm:text-sm"
          >
            Login
          </a>

          <button
            onClick={() => goTo("/complete-profile")}
            className="inline-flex min-h-10 items-center justify-center rounded-lg bg-blue-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-950 sm:px-5 sm:text-sm"
          >
            Get started
          </button>
        </div>
      </div>
    </header>
  );
}

function SectionTitle({ label, title, text, center = false }) {
  return (
    <div
      className={`reveal mb-10 md:mb-12 ${
        center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"
      }`}
    >
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.32em] text-blue-600">
        {label}
      </p>

      <h2 className="text-3xl font-semibold leading-tight text-slate-950 md:text-5xl">
        {title}
      </h2>

      {text && <p className="mt-5 leading-8 text-slate-600">{text}</p>}
    </div>
  );
}

/* =====================================================
   HERO — headline + orbiting-logo visual with parallax
===================================================== */

function HeroVisual() {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -8, y: px * 12 });
  };
  const handleLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="relative mx-auto flex items-center justify-center"
      style={{ height: 360, width: 360, perspective: 900 }}
    >
      <div
        className="absolute rounded-full bg-blue-200"
        style={{
          width: 260,
          height: 260,
          left: "0%",
          top: "0%",
          filter: "blur(46px)",
          opacity: 0.5,
          animation: "bsBlob 9s ease-in-out infinite",
        }}
      />
      <div
        className="absolute rounded-full bg-orange-200"
        style={{
          width: 220,
          height: 220,
          right: "0%",
          bottom: "0%",
          filter: "blur(42px)",
          opacity: 0.55,
          animation: "bsBlob 11s ease-in-out infinite reverse",
        }}
      />

      <div
        className="relative"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: "transform 0.25s ease-out",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          style={{
            width: 300,
            height: 300,
            position: "relative",
            animation: "bsSpin 22s linear infinite",
          }}
        >
          {ORBIT_COLORS.map((col, i) => {
            const angle = (360 / ORBIT_COLORS.length) * i;
            return (
              <div
                key={col}
                className="absolute rounded-full shadow-md"
                style={{
                  width: 14,
                  height: 14,
                  background: col,
                  top: "50%",
                  left: "50%",
                  transform: `rotate(${angle}deg) translate(140px) rotate(${-angle}deg)`,
                }}
              />
            );
          })}
        </div>

        <div
          className="absolute flex items-center justify-center rounded-full border border-white/70 bg-white/60 shadow-xl backdrop-blur"
          style={{
            width: 170,
            height: 170,
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
          }}
        >
          <img src={logo} alt="Bharat Sangam" className="h-24 w-auto" />
        </div>

        <div
          className="absolute flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-800 shadow-lg"
          style={{ right: -14, bottom: 0, animation: "bsFloat 5s ease-in-out infinite" }}
        >
          <span
            className="h-2 w-2 rounded-full bg-blue-600"
            style={{ animation: "bsPulse 1.8s ease-in-out infinite" }}
          />
          Live updates from your clubs
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-white">
      <style>{`
        @keyframes bsSpin { to { transform: rotate(360deg); } }
        @keyframes bsBlob { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(14px,-10px) scale(1.06); } }
        @keyframes bsFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes bsPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.7); } }
      `}</style>

      <div className="absolute left-1/2 top-20 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-50 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 md:py-16 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:py-20">
        <div className="reveal text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-blue-600">
            Community · Clubs · Connection
          </p>

          <h1 className="mt-6 max-w-2xl text-3xl font-semibold leading-[1.2] tracking-tight text-slate-950 sm:text-4xl lg:text-[38px]">
            One home for your club — members, meetings, and everything
            between.
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-8 text-slate-600">
            Youth clubs, associations, community trusts and federations run
            their whole organization on Bharat Sangam — members, tasks,
            events and income — while anyone, member or not, can follow,
            connect and stay in the loop.
          </p>

         <div className="mt-8 flex flex-wrap justify-start gap-4">
  <a
    href="https://club.bsangam.com/client-register"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center justify-center rounded-lg bg-blue-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-950"
  >
    Bring your club online
  </a>

  <button
    type="button"
    onClick={() => goTo("/public-clubs-map")}
    className="rounded-lg border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition hover:border-blue-200 hover:bg-blue-50"
  >
    Browse clubs
  </button>
</div>
        </div>

        <div className="reveal">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

/* =====================================================
   CLUB TYPES STRIP
===================================================== */

function ClubTypes() {
  return (
    <section className="bg-slate-900 px-5 py-10">
      <div className="mx-auto max-w-7xl">
        <p className="reveal mb-5 text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">
          Built for every kind of community
        </p>
        <div className="reveal flex flex-wrap gap-3">
          {clubTypes.map((t) => (
            <span
              key={t}
              className="rounded-full border border-white/20 px-4 py-2 text-sm text-white"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================
   MANAGE + EARN — two-column, matches the artifact's
   signature section with the sponsored-post mockup
===================================================== */

function ManageEarn() {
  return (
    <section id="about" className="bg-blue-950 px-5 py-16 md:py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
        <div className="reveal">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.32em] text-orange-300">
            Manage. Communicate. Earn.
          </p>
          <h2 className="text-3xl font-semibold leading-tight text-white md:text-4xl">
            Every club task in one place — and a way to fund it too
          </h2>
          <p className="mt-5 leading-8 text-blue-100">
            Bharat Sangam handles the everyday running of a club — member
            records, meetings, events and news — so office-bearers spend less
            time chasing updates. And when a club carries an advertisement
            inside its news feed or posts, that club earns from it. Running
            the community can pay for itself.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-white">
            {[
              "One member directory instead of five spreadsheets",
              "Tasks and approvals tracked, not lost in chat threads",
              "Sponsored posts and ads inside your feed, revenue back to the club",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="reveal">
          <ManageEarnIllustration />
        </div>
      </div>
    </section>
  );
}

function ManageEarnIllustration() {
  return (
    <div className="relative flex w-full items-center justify-center lg:justify-end">
      <style>{`
        @keyframes whoForFloat {
          0%, 100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-8px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .who-for-illustration {
            animation: none !important;
          }
        }
      `}</style>

      <img
        src={whoForImage}
        alt="Bharat Sangam community management illustration"
        className="
          who-for-illustration
          h-auto
          w-[115%]
          max-w-none
          object-contain
          drop-shadow-[0_18px_35px_rgba(15,23,42,0.28)]
          sm:w-[108%]
          lg:w-[125%]
          lg:translate-x-10
          xl:w-[135%]
          xl:translate-x-16
        "
        style={{
          maxHeight: "560px",
          animation: "whoForFloat 5s ease-in-out infinite",
        }}
      />
    </div>
  );
}
function Features() {
  return (
    <section id="features" className="bg-white px-5 py-16 md:py-20">
      <div className="mx-auto max-w-7xl">
        <SectionTitle
          label="Everything the club needs"
          title="Thirteen jobs, one login"
        />

        <div className="grid gap-x-10 gap-y-7 md:grid-cols-2 lg:grid-cols-3">
          {features.map(([Icon, title, text]) => (
            <div
              key={title}
              className="reveal flex gap-5 border-b border-slate-100 pb-7"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <Icon size={21} />
              </div>

              <div>
                <h3 className="font-semibold text-slate-950">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================
   AUDIENCE — For Members / For Clubs / For Guest Users
===================================================== */

function Audience() {
  const audiences = [
    {
      image: userImage,
      eyebrow: "Personal experience",
      title: "For Members",
      copy:
        "Stay connected with every club you belong to from one profile. Follow updates, attend events, join meetings and participate in community discussions.",
      points: [
        "Follow club news and announcements",
        "Join events, meetings and discussions",
        "Use one profile across every club",
        "Connect directly with other members",
      ],
      accent: "blue",
    },
    {
      image: adminImage,
      eyebrow: "Complete club management",
      title: "For Clubs",
      copy:
        "Bring members, office-bearers, meetings, events, records and communication into one organised workspace built for everyday club operations.",
      points: [
        "Manage members, groups and roles",
        "Run meetings, events and forms",
        "Publish news, posts and media",
        "Generate income through advertisements",
      ],
      accent: "orange",
    },
    {
      image: organizationImage,
      eyebrow: "Open community access",
      title: "For Guest Users",
      copy:
        "Explore communities without becoming a member. Follow public clubs, discover their work, connect with people and view public updates.",
      points: [
        "Discover and follow public clubs",
        "View public profiles and updates",
        "Connect and chat with people",
        "Stay informed without membership",
      ],
      accent: "violet",
    },
  ];

  return (
    <section
      id="users"
      className="relative overflow-hidden bg-slate-50 px-4 py-10 sm:px-5 md:py-12"
    >
      <div className="absolute left-0 top-0 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-100/60 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-60 w-60 translate-x-1/3 translate-y-1/3 rounded-full bg-orange-100/60 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl">
        <div className="[&_.reveal]:mb-7 [&_p:first-child]:mb-2 [&_h2]:mx-auto [&_h2]:max-w-3xl [&_h2]:text-2xl sm:[&_h2]:text-3xl lg:[&_h2]:text-[32px] [&_h2]:leading-tight [&_p:last-child]:mt-3 [&_p:last-child]:text-sm [&_p:last-child]:leading-6">
          <SectionTitle
            label="Who it's for"
            title="One platform, designed around every person in the community"
            text="Members get a simple connected experience, clubs get complete control, and visitors can explore communities openly."
            center
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {audiences.map((audience, index) => (
            <AudienceCard
              key={audience.title}
              {...audience}
              featured={index === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
function AudienceCard({
  image,
  eyebrow,
  title,
  copy,
  points,
  accent,
}) {
  const styles = {
    blue: {
      badge: "bg-blue-50 text-blue-700",
      icon: "text-blue-600",
      border: "hover:border-blue-200",
    },
    orange: {
      badge: "bg-orange-50 text-orange-700",
      icon: "text-orange-600",
      border: "hover:border-orange-200",
    },
    violet: {
      badge: "bg-violet-50 text-violet-700",
      icon: "text-violet-600",
      border: "hover:border-violet-200",
    },
  };

  const theme = styles[accent];

  return (
    <article
      className={`reveal group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md ${theme.border}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-[86px] w-[100px] shrink-0 items-center justify-center">
          <img
            src={image}
            alt={title}
            className="max-h-[82px] max-w-[96px] object-contain transition duration-300 group-hover:scale-[1.02]"
          />
        </div>

        <div className="min-w-0 flex-1 pt-1">
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-[8px] font-semibold uppercase tracking-[0.12em] ${theme.badge}`}
          >
            {eyebrow}
          </span>

          <h3 className="mt-2 text-lg font-semibold leading-tight text-slate-950">
            {title}
          </h3>
        </div>
      </div>

      <p className="mt-4 text-xs leading-5 text-slate-600">
        {copy}
      </p>

      <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
        {points.map((point) => (
          <div
            key={point}
            className="flex items-start gap-2 text-xs leading-5 text-slate-700"
          >
            <CheckCircle2
              className={`mt-0.5 h-4 w-4 shrink-0 ${theme.icon}`}
            />

            <span>{point}</span>
          </div>
        ))}
      </div>
    </article>
  );
}
/* =====================================================
   OPEN TO EVERYONE
===================================================== */
function OpenToEveryone() {
  const networkMembers = [
    {
      position: "left-[8%] top-[15%]",
      color: "bg-cyan-400",
      delay: "0s",
    },
    {
      position: "left-[47%] top-[3%]",
      color: "bg-blue-400",
      delay: "0.3s",
    },
    {
      position: "right-[8%] top-[18%]",
      color: "bg-violet-400",
      delay: "0.6s",
    },
    {
      position: "left-[6%] bottom-[18%]",
      color: "bg-red-400",
      delay: "0.9s",
    },
    {
      position: "left-[49%] bottom-[4%]",
      color: "bg-pink-400",
      delay: "1.2s",
    },
    {
      position: "right-[10%] bottom-[14%]",
      color: "bg-orange-400",
      delay: "1.5s",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-12 sm:px-5 md:py-16">
      <style>{`
        @keyframes openNetworkPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }

          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes openNetworkLine {
          0%, 100% {
            opacity: 0.3;
          }

          50% {
            opacity: 0.75;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .open-network-node,
          .open-network-line {
            animation: none !important;
          }
        }
      `}</style>

      {/* Background decorations */}
      <div className="pointer-events-none absolute left-0 top-0 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-100/60 blur-3xl" />

      <div className="pointer-events-none absolute bottom-0 right-0 h-72 w-72 translate-x-1/3 translate-y-1/3 rounded-full bg-violet-100/40 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          {/* Left content */}
          <div className="reveal">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
              Not just for members
            </p>

            <h2 className="mt-4 max-w-xl text-3xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-4xl lg:text-[40px]">
              Anyone can explore, follow and connect
            </h2>

            <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8">
              Bharat Sangam is open to everyone. You do not need to belong to
              a club to discover communities, follow public updates, connect
              directly with people or view their public profiles.
            </p>
          </div>

          {/* Right network design */}
          <div className="reveal">
            <div className="mx-auto w-full max-w-[560px]">
              <div className="relative mx-auto h-[220px] max-w-[410px] sm:h-[250px]">
                <svg
                  viewBox="0 0 410 250"
                  className="absolute inset-0 h-full w-full"
                  aria-hidden="true"
                >
                  {[
                    [205, 125, 48, 45],
                    [205, 125, 205, 25],
                    [205, 125, 360, 55],
                    [205, 125, 45, 205],
                    [205, 125, 210, 230],
                    [205, 125, 360, 205],
                  ].map(([x1, y1, x2, y2], index) => (
                    <line
                      key={index}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="#D6E2F1"
                      strokeWidth="2"
                      strokeLinecap="round"
                      className="open-network-line"
                      style={{
                        animation: `openNetworkLine 3s ease-in-out ${
                          index * 0.25
                        }s infinite`,
                      }}
                    />
                  ))}
                </svg>

                {/* Center circle */}
                <div className="absolute left-1/2 top-1/2 z-20 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border-[6px] border-blue-100 bg-blue-900 text-white shadow-xl shadow-blue-900/20">
                  <Users size={25} />

                  <span className="mt-1 text-[10px] font-semibold uppercase tracking-wider">
                    You
                  </span>
                </div>

                {/* Outer network circles */}
                {networkMembers.map(
                  ({ position, color, delay }, index) => (
                    <div
                      key={index}
                      className={`open-network-node absolute z-10 flex h-11 w-11 items-center justify-center rounded-full border-4 border-white shadow-lg ${position} ${color}`}
                      style={{
                        animation: `openNetworkPulse 3s ease-in-out ${delay} infinite`,
                      }}
                    >
                      <Users size={15} className="text-white" />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
/* =====================================================
   MOBILE APP
===================================================== */

function MobileApp() {
  return (
    <section id="mobile-app" className="bg-white px-4 py-8 md:px-5 md:py-12">
      <div className="mx-auto max-w-5xl">
        <div className="reveal rounded-2xl border border-slate-100 bg-white px-5 py-8 shadow-sm md:px-8 md:py-10">
          <div className="grid items-center gap-8 md:grid-cols-[220px_1fr] lg:grid-cols-[250px_1fr]">
            <div className="flex justify-center md:justify-start">
              <img
                src={mobileMockup}
                alt="Bharat Sangam mobile app mockup"
                className="h-[200px] w-auto object-contain drop-shadow-lg sm:h-[230px] md:h-[260px] lg:h-[285px]"
              />
            </div>

            <div className="text-center md:text-left">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-orange-600">
                Mobile app
              </p>

              <h2 className="mx-auto mt-3 max-w-2xl text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl md:mx-0 md:text-4xl">
                Take your club with you, anywhere
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-600 md:mx-0 md:text-base">
                Available now on Android. iOS is on the way. Explore clubs,
                follow communities, view posts, join events and stay connected
                from one simple mobile app.
              </p>

              <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row md:justify-start">
                <a
                  href="https://play.google.com/store/apps/details?id=com.bharathsangam.user"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 rounded-xl bg-black px-5 py-3 text-white shadow-sm transition hover:bg-slate-900 sm:min-w-[180px]"
                >
                  <span className="text-base">▶</span>

                  <span className="text-left">
                    <span className="block text-[9px] uppercase leading-none text-white/70">
                      Get it on
                    </span>

                    <span className="text-sm font-semibold">
                      Google Play
                    </span>
                  </span>
                </a>

                <span className="inline-flex cursor-not-allowed items-center justify-center gap-3 rounded-xl bg-black px-5 py-3 text-white opacity-75 shadow-sm sm:min-w-[180px]">
                  <span className="text-base"></span>

                  <span className="text-left">
                    <span className="block text-[9px] uppercase leading-none text-white/70">
                      Coming soon on
                    </span>

                    <span className="text-sm font-semibold">
                      App Store
                    </span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =====================================================
   PRICING
===================================================== */

function PricingPage() {
  useReveal();

  const plans = [
    ["Starter", "For small clubs", "₹999"],
    ["Growth", "For active organizations", "₹2,499"],
    ["Enterprise", "For large associations", "Custom"],
  ];

  return (
    <main className="bg-white px-5 py-16 md:py-20">
      <div className="mx-auto max-w-7xl">
        <SectionTitle
          label="Pricing"
          title="Simple plans for every community"
          text="Choose a plan based on your club, organization or association size."
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {plans.map(([name, text, price]) => (
            <div
              key={name}
              className="reveal rounded-2xl border border-slate-100 bg-white p-7 shadow-sm"
            >
              <h3 className="text-2xl font-semibold text-slate-950">{name}</h3>

              <p className="mt-2 text-slate-600">{text}</p>

              <h4 className="mt-8 text-4xl font-semibold text-blue-900">
                {price}
              </h4>

              <button className="mt-8 inline-flex rounded-lg bg-blue-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-950">
                Get Started
              </button>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

/* =====================================================
   POLICY PAGES (unchanged)
===================================================== */

function PolicyPage({ type }) {
  useReveal();

  const pages = {
    privacy: {
      title: "Privacy Policy",
      intro:
        "This Privacy Policy explains how Bharat Sangam collects, uses, stores and protects information when users, members, clubs, organizations and client admins use the platform.",
      sections: [
        {
          heading: "1. Information We Collect",
          points: [
            "Basic account details such as name, mobile number, email address and profile information.",
            "Community, club or organization details such as club name, address, leadership information, contact details and profile content.",
            "Member-related information added by client admins for community management purposes.",
            "Content uploaded to the platform such as posts, media, documents, event details and advertisements.",
            "Technical information such as browser type, device details, IP address and usage activity for security and service improvement.",
          ],
        },
        {
          heading: "2. How We Use Information",
          points: [
            "To create and manage user, club and organization accounts.",
            "To help client admins manage members, events, posts, media, documents and reports.",
            "To improve platform performance, security and user experience.",
            "To communicate service updates, support responses and important platform notifications.",
            "To prevent misuse, unauthorized access, fraud or policy violations.",
          ],
        },
        {
          heading: "3. Data Sharing",
          points: [
            "We do not sell personal information to third parties.",
            "Information may be visible to users based on platform visibility settings such as public, followers, members or private.",
            "We may share limited information with service providers only when required to operate the platform.",
            "We may disclose information if required by law, legal process or government request.",
          ],
        },
        {
          heading: "4. Data Security",
          points: [
            "We use reasonable technical and organizational measures to protect data.",
            "Client admins are responsible for maintaining access control and using strong login credentials.",
            "No digital system is completely risk-free, but we work to reduce unauthorized access and misuse.",
          ],
        },
        {
          heading: "5. User Rights",
          points: [
            "Users may request correction of inaccurate profile information.",
            "Users may request account deletion by contacting support.",
            "Client admins may manage member data according to their organization policies and applicable law.",
          ],
        },
      ],
    },

    terms: {
      title: "Terms & Conditions",
      intro:
        "These Terms & Conditions govern the use of Bharat Sangam by users, members, client admins, clubs, associations and organizations.",
      sections: [
        {
          heading: "1. Acceptance of Terms",
          points: [
            "By accessing or using Bharat Sangam, you agree to follow these Terms & Conditions.",
            "If you are using the platform on behalf of a club or organization, you confirm that you have permission to do so.",
            "If you do not agree with these terms, you should not use the platform.",
          ],
        },
        {
          heading: "2. Platform Usage",
          points: [
            "Users must provide accurate information during registration and profile updates.",
            "Client admins are responsible for managing their organization profile, members, content and permissions.",
            "The platform must not be used for unlawful, harmful, misleading, abusive or unauthorized activities.",
            "Users must not upload content that violates copyright, privacy, legal rights or community standards.",
          ],
        },
        {
          heading: "3. Account Responsibility",
          points: [
            "Users and client admins are responsible for keeping login details secure.",
            "Any activity performed through an account may be treated as activity by the account holder.",
            "Suspicious activity should be reported to Bharat Sangam support immediately.",
          ],
        },
        {
          heading: "4. Content Responsibility",
          points: [
            "Clubs, organizations and users are responsible for the content they publish.",
            "Bharat Sangam may remove or restrict content that violates platform rules or applicable law.",
            "Published content may be visible based on selected visibility and privacy settings.",
          ],
        },
        {
          heading: "5. Service Changes",
          points: [
            "Bharat Sangam may update, improve, modify or discontinue features from time to time.",
            "We may update these Terms & Conditions when required.",
            "Continued use of the platform after changes means you accept the updated terms.",
          ],
        },
      ],
    },

    cancellation: {
      title: "Return Cancellation Policy",
      intro:
        "This Return Cancellation Policy explains how subscription cancellation, refund and service-related payment requests are handled for Bharat Sangam.",
      sections: [
        {
          heading: "1. Subscription Cancellation",
          points: [
            "Clients may request cancellation of a subscription by contacting Bharat Sangam support.",
            "Cancellation requests should include organization name, registered email or mobile number, plan details and reason for cancellation.",
            "Cancellation will be processed after verification of account and payment details.",
          ],
        },
        {
          heading: "2. Refund Eligibility",
          points: [
            "Refund eligibility depends on the selected plan, billing cycle, service usage and payment status.",
            "Setup charges, customization charges, onboarding charges or third-party service charges may be non-refundable.",
            "Refunds may not be applicable after substantial usage of the platform during the billing period.",
          ],
        },
        {
          heading: "3. Processing Time",
          points: [
            "Approved refund requests may take several business days to process.",
            "Refunds will usually be processed to the original payment method, subject to payment gateway and banking timelines.",
            "Bharat Sangam is not responsible for delays caused by banks, payment gateways or third-party providers.",
          ],
        },
        {
          heading: "4. Failed or Duplicate Payments",
          points: [
            "If a payment fails but the amount is debited, users should contact support with transaction details.",
            "Duplicate payments will be reviewed and refunded if verified successfully.",
            "Payment confirmation may require bank, gateway or transaction reference details.",
          ],
        },
        {
          heading: "5. Contact for Cancellation",
          points: [
            "For cancellation or refund support, contact Bharat Sangam using the email or phone number mentioned below.",
            "Please include registered club or organization name, username, payment date and transaction reference if available.",
          ],
        },
      ],
    },

    delete: {
      title: "Delete Account",
      intro:
        "Users and client admins can request deletion of their Bharat Sangam account by contacting support. Account deletion is processed after verification.",
      sections: [
        {
          heading: "1. How to Request Account Deletion",
          points: [
            "Send an email to Bharat Sangam support from your registered email address.",
            "Mention your name, registered mobile number, club or organization name if applicable, and username.",
            "Use the subject line: Account Deletion Request.",
          ],
        },
        {
          heading: "2. Verification",
          points: [
            "Bharat Sangam may verify your identity before processing account deletion.",
            "For organization or client admin accounts, authorization may be required before deletion.",
            "Requests with incomplete or incorrect details may take longer to process.",
          ],
        },
        {
          heading: "3. What Happens After Deletion",
          points: [
            "Profile access may be removed or disabled.",
            "Personal information may be deleted or anonymized as per applicable policy and legal requirements.",
            "Some records may be retained where required for billing, audit, legal, security or compliance purposes.",
          ],
        },
      ],
    },
  };

  const page = pages[type];

  return (
    <main className="bg-white px-5 py-16 md:py-20">
      <div className="reveal mx-auto max-w-5xl">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm md:p-8">
          <div className="flex items-start gap-4">
            {type === "delete" ? (
              <Trash2 className="mt-1 h-9 w-9 shrink-0 text-blue-700" />
            ) : (
              <FileText className="mt-1 h-9 w-9 shrink-0 text-blue-700" />
            )}

            <div>
              <h1 className="text-3xl font-semibold text-slate-950 md:text-5xl">
                {page.title}
              </h1>
              <p className="mt-5 max-w-3xl leading-8 text-slate-600">
                {page.intro}
              </p>
            </div>
          </div>

          <div className="mt-10 space-y-7">
            {page.sections.map((section) => (
              <section
                key={section.heading}
                className="rounded-2xl bg-slate-50 p-5 md:p-6"
              >
                <h2 className="text-xl font-semibold text-slate-950">
                  {section.heading}
                </h2>

                <div className="mt-4 space-y-3">
                  {section.points.map((point) => (
                    <p
                      key={point}
                      className="flex gap-3 text-sm leading-7 text-slate-600"
                    >
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-blue-600" />
                      <span>{point}</span>
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-slate-100 bg-white p-5 md:p-6">
            <h2 className="text-xl font-semibold text-slate-950">
              Contact Support
            </h2>

            <div className="mt-4 space-y-2 text-slate-600">
              <p className="font-semibold text-slate-950">{contact.company}</p>
              <p>
                Email:{" "}
                <a
                  href={supportMailto(
                    type === "delete"
                      ? "Account Deletion Request"
                      : "Bharat Sangam Support Request"
                  )}
                  className="font-semibold text-blue-700 hover:underline"
                >
                  {contact.email}
                </a>
              </p>
              <p>Phone: {contact.phone}</p>
              <p>Address: {contact.address}</p>
            </div>
          </div>

          <p className="mt-8 text-sm leading-7 text-slate-500">
            Last updated: 2026. This page is provided for general platform
            information and may be updated from time to time.
          </p>
        </div>
      </div>
    </main>
  );
}

/* =====================================================
   FOOTER — company name added to Contact block
===================================================== */
/* =====================================================
   FOOTER
===================================================== */

/* =====================================================
   FOOTER
===================================================== */

function Footer() {
  const footerLinks = [
    ["Privacy Policy", "/privacy-policy"],
    ["Terms & Conditions", "/terms-and-conditions"],
    ["Return Cancellation Policy", "/return-cancellation-policy"],
    ["Delete Account", "/delete-account"],
  ];

  return (
    <footer id="contact" className="bg-blue-950 text-white">
      <div className="mx-auto max-w-7xl px-5 py-10 md:py-12">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.15fr_1.35fr_0.9fr_0.9fr] lg:gap-12">
          {/* Brand */}
          <div>
            <button
              type="button"
              onClick={() => goTo("/")}
              className="inline-flex"
              aria-label="Go to Bharat Sangam home"
            >
              <img
                src={footerLogo}
                alt="Bharat Sangam"
                className="h-24 w-auto object-contain sm:h-28 lg:h-32"
              />
            </button>

            <p className="mt-3 max-w-[300px] text-sm leading-7 text-blue-100">
              Uniting communities, empowering Bharat. One place for clubs to
              connect, collaborate and grow.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-base font-semibold text-white">
              Contact Us
            </h3>

            <div className="mt-5 space-y-4 text-sm leading-6 text-blue-100">
              <p className="font-semibold text-white">
                {contact.company}
              </p>

              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-200" />

                <a
                  href={supportMailto()}
                  className="break-all transition hover:text-white hover:underline"
                >
                  {contact.email}
                </a>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-blue-200" />

                <a
                  href={`tel:${contact.phone.replace(/\s+/g, "")}`}
                  className="transition hover:text-white"
                >
                  {contact.phone}
                </a>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-200" />

                <span className="max-w-[310px]">
                  {contact.address}
                </span>
              </div>

              <p className="font-semibold text-white">
                GSTIN: {contact.gstin}
              </p>
            </div>
          </div>

          {/* App availability */}
          <div>
            <h3 className="text-base font-semibold text-white">
              App Availability
            </h3>

          <div className="mt-5 space-y-3 text-sm text-blue-100">
  <a
    href="https://play.google.com/store/apps/details?id=com.bharathsangam.user"
    target="_blank"
    rel="noopener noreferrer"
    className="group flex w-fit items-center gap-3 transition hover:text-white"
  >
    <span className="h-2 w-2 rounded-full bg-green-400" />

    <span className="group-hover:underline">
      Available on Android
    </span>
  </a>

  <div className="flex items-center gap-3">
    <span className="h-2 w-2 rounded-full bg-orange-400" />

    <span>iOS coming soon</span>
  </div>
</div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-base font-semibold text-white">
              Quick Links
            </h3>

            <nav className="mt-5 grid gap-3 text-sm text-blue-100">
              {footerLinks.map(([name, path]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => goTo(path)}
                  className="w-fit text-left transition hover:translate-x-1 hover:text-white"
                >
                  {name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-5 text-xs text-blue-100 sm:text-sm lg:flex-row lg:items-center lg:justify-between">
          <p>
            © 2026 {contact.company.toUpperCase()}. All rights reserved.
          </p>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {footerLinks.map(([name, path], index) => (
              <React.Fragment key={name}>
                <button
                  type="button"
                  onClick={() => goTo(path)}
                  className="transition hover:text-white"
                >
                  {name}
                </button>

                {index < footerLinks.length - 1 && (
                  <span className="hidden text-white/30 sm:inline">
                    |
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
/* =====================================================
   HOME PAGE ASSEMBLY
===================================================== */

function HomePage() {
  useReveal();

  return (
    <>
      <Hero />
      <ClubTypes />
      <Audience />
      <ManageEarn />
      <Features />
      <OpenToEveryone />
      <MobileApp />
    </>
  );
}

export default function WebsitePage() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);

    return () => window.removeEventListener("popstate", onPop);
  }, []);

  let page = <HomePage />;

  if (path === "/pricing") page = <PricingPage />;
  if (path === "/privacy-policy") page = <PolicyPage type="privacy" />;
  if (path === "/terms-and-conditions") page = <PolicyPage type="terms" />;
  if (path === "/return-cancellation-policy") {
    page = <PolicyPage type="cancellation" />;
  }
  if (path === "/delete-account") page = <PolicyPage type="delete" />;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />
      {page}
      <Footer />
    </div>
  );
}